import useAdminOperation from "../hooks/useAdminOperation";
import "./AdminSecondary.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Save, Trash2, UploadCloud, X } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { API, api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const STATUS_OPTIONS = ["Draft", "Published", "Canceled"];

const EMPTY_FORM = {
  event_name: "",
  event_date: "",
  event_dates: [],
  start_time: "",
  end_time: "",
  location_name: "",
  street_address: "",
  city: "",
  state: "",
  zip_code: "",
  booth_number: "",
  short_description: "",
  long_description: "",
  event_website_link: "",
  facebook_event_link: "",
  event_image_filename: "",
  status: "Draft",
  featured_event: false,
  show_canceled_public: false,
};

function byDate(a, b) {
  return String(a.event_date || "").localeCompare(String(b.event_date || ""));
}

function fmtDate(date) {
  if (!date) return "";
  const [year, month, day] = String(date).split("-").map(Number);
  if (!year || !month || !day) return date;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function mapPreviewAddress(form) {
  return [form.street_address, form.city, form.state, form.zip_code].filter(Boolean).join(", ");
}

function dateKeyToLocal(key) {
  const [year, month, day] = String(key || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function localDateToKey(date) {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function toPayload(form) {
  const dates = [...new Set([form.event_date, ...(form.event_dates || [])].filter(Boolean))].sort();
  return {
    ...form,
    event_date: dates[0] || form.event_date,
    event_dates: dates,
    state: form.state?.trim() || "",
    zip_code: form.zip_code?.trim() || "",
    booth_number: form.booth_number?.trim() || null,
    long_description: form.long_description?.trim() || null,
    event_website_link: form.event_website_link?.trim() || null,
    facebook_event_link: form.facebook_event_link?.trim() || null,
    event_image_filename: form.event_image_filename?.trim() || null,
  };
}

export default function AdminEvents() {
  const beginOperation = useAdminOperation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  useEffect(() => { if (editorOpen) document.getElementById("admin-event-editor")?.scrollIntoView?.({ behavior: "auto", block: "start" }); }, [editorOpen]);
  const [previewingId, setPreviewingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  const selectedDates = useMemo(
    () => (form.event_dates || []).map(dateKeyToLocal).filter(Boolean),
    [form.event_dates],
  );

  const toggleDay = (day) => {
    if (!day) return;
    const key = localDateToKey(day);
    setForm((prev) => {
      const current = new Set(prev.event_dates || []);
      if (current.has(key)) current.delete(key); else current.add(key);
      const dates = [...current].sort();
      return { ...prev, event_dates: dates, event_date: dates[0] || "" };
    });
  };

  const removeDate = (key) => {
    setForm((prev) => {
      const dates = (prev.event_dates || []).filter((d) => d !== key);
      return { ...prev, event_dates: dates, event_date: dates[0] || "" };
    });
  };

  const loadEvents = useCallback(async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setLoading(true);
    try {
      const res = await api.get("/admin/events");
      if (!operationCurrent()) return;
      setEvents([...(res.data || [])].sort(byDate));
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setLoading(false);
      }
    }
  }, [beginOperation]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const selectedPreview = useMemo(() => {
    if (previewingId === "draft-preview") {
      return {
        id: "draft-preview",
        ...form,
        full_address: mapPreviewAddress(form),
      };
    }
    return events.find((event) => event.id === previewingId) || null;
  }, [events, form, previewingId]);

  const applyField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
    setPreviewingId("");
  };

  const startEdit = (event) => {
    setEditorOpen(true);
    setEditingId(event.id);
    const dates = [...new Set([event.event_date, ...(event.event_dates || [])].filter(Boolean))].sort();
    setForm({
      event_name: event.event_name || "",
      event_date: event.event_date || "",
      event_dates: dates,
      start_time: event.start_time || "",
      end_time: event.end_time || "",
      location_name: event.location_name || "",
      street_address: event.street_address || "",
      city: event.city || "",
      state: event.state || "",
      zip_code: event.zip_code || "",
      booth_number: event.booth_number || "",
      short_description: event.short_description || "",
      long_description: event.long_description || "",
      event_website_link: event.event_website_link || "",
      facebook_event_link: event.facebook_event_link || "",
      event_image_filename: event.event_image_filename || "",
      status: event.status || "Draft",
      featured_event: Boolean(event.featured_event),
      show_canceled_public: Boolean(event.show_canceled_public),
    });
    setPreviewingId(event.id);
  };

  const submitForm = async (e, forceStatus = null) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    e.preventDefault();
    const payload = toPayload(form);

    if (!payload.event_name?.trim() || !payload.location_name?.trim() || !payload.city?.trim() || !payload.state?.trim() || !payload.zip_code?.trim()) {
      toast.error("Please complete the event name, location, city, state, and ZIP before saving.");
      return;
    }

    const zipCode = payload.zip_code.trim();
    if (!/^\d{5}(?:-\d{4})?$|^[A-Za-z0-9]{4,10}$/.test(zipCode)) {
      toast.error("Enter a valid ZIP code before publishing this event.");
      return;
    }

    setSaving(true);
    try {
      if (forceStatus) {
        payload.status = forceStatus;
      }
      if (editingId) {
        await api.patch(`/admin/events/${editingId}`, payload);
      if (!operationCurrent()) return;
        toast.success("Event updated.");
      } else {
        await api.post("/admin/events", payload);
      if (!operationCurrent()) return;
        toast.success("Event created.");
      }
      await loadEvents();
      if (!operationCurrent()) return;
      resetForm();
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const submitWithStatus = (status) => (e) => submitForm(e, status);

  const deleteEvent = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const ok = window.confirm(`Delete ${event.event_name}? This cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/admin/events/${event.id}`);
      if (!operationCurrent()) return;
      toast.success("Event deleted.");
      if (editingId === event.id) resetForm();
      await loadEvents();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const updatePublishState = async (event, action) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    try {
      if (action === "publish") await api.post(`/admin/events/${event.id}/publish`);
      if (!operationCurrent()) return;
      if (action === "unpublish") await api.post(`/admin/events/${event.id}/unpublish`);
      if (!operationCurrent()) return;
      if (action === "cancel") await api.post(`/admin/events/${event.id}/cancel`, null, { params: { show_publicly: true } });
      if (!operationCurrent()) return;
      toast.success("Event status updated.");
      await loadEvents();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const uploadFile = async (ev) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const file = ev.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/admin/events/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!operationCurrent()) return;
      applyField("event_image_filename", res.data.filename);
      toast.success("Image/flyer uploaded.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setUploading(false);
      ev.target.value = "";
      }
    }
  };

  return (
    <div className="bbw-admin-secondary min-h-screen" data-testid="admin-events-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-2">Admin Dashboard</p>
          <h1 className="font-display text-4xl text-[#F5F0E6]">Manage Events</h1>
        </div>
        <div className="admin-events-header-actions flex flex-wrap gap-3">
          <Button onClick={() => { resetForm(); setEditorOpen(true); }} className="bbw-admin-primary">
            Add New Event
          </Button>
          <Button onClick={loadEvents} variant="ghost" className="text-stone-300 hover:text-stone-100">Refresh</Button>
          <Link to="/events" className="admin-secondary-quiet-link">View public events <span aria-hidden="true">↗︎</span></Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100/10">
            <h2 className="font-display text-2xl text-stone-100">All Events</h2>
          </div>
          {loading ? <p className="p-5 text-stone-400">Loading events...</p> : null}
          {!loading && events.length === 0 ? <p className="p-5 text-stone-400">No events created yet.</p> : null}
          {!loading && events.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-events-table w-full text-left text-sm">
                <thead className="bg-zinc-950/60 text-stone-300">
                  <tr>
                    <th className="px-4 py-3">Event Name</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Start</th>
                    <th className="px-4 py-3">End</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">City/State</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Featured</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-t border-stone-100/10">
                      <td data-label="Event name" className="px-4 py-3 text-stone-100">{event.event_name}</td>
                      <td data-label="Date" className="px-4 py-3 text-stone-300">{fmtDate(event.event_date)}</td>
                      <td data-label="Start" className="px-4 py-3 text-stone-300">{event.start_time}</td>
                      <td data-label="End" className="px-4 py-3 text-stone-300">{event.end_time}</td>
                      <td data-label="Location" className="px-4 py-3 text-stone-300">{event.location_name}</td>
                      <td data-label="City / state" className="px-4 py-3 text-stone-300">{event.city}, {event.state}</td>
                      <td data-label="Status" className="px-4 py-3 text-stone-300">{event.status}</td>
                      <td data-label="Featured" className="px-4 py-3 text-stone-300">{event.featured_event ? "Yes" : "No"}</td>
                      <td data-label="Actions" className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => startEdit(event)} className="bg-transparent border-stone-100/15 text-stone-200">
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setPreviewingId(event.id); setEditorOpen(true); }} className="bg-transparent border-stone-100/15 text-stone-200">
                            <Eye className="w-3 h-3 mr-1" /> Preview
                          </Button>
                          {event.status === "Published" ? (
                            <Button size="sm" variant="outline" onClick={() => updatePublishState(event, "unpublish")} className="bg-transparent border-stone-100/15 text-stone-200">
                              Unpublish
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => updatePublishState(event, "publish")} className="bg-transparent border-stone-100/15 text-stone-200">
                              Publish
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => updatePublishState(event, "cancel")} className="bg-transparent border-rose-500/30 text-rose-300">
                            Cancel
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteEvent(event)} className="bg-transparent border-rose-500/30 text-rose-300">
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>
<details id="admin-event-editor" className="admin-secondary-editor" open={editorOpen} onToggle={(event) => setEditorOpen(event.currentTarget.open)}>
<summary><div>{editingId ? "Edit selected event" : "Create an event"}<span>Dates, location, image and publishing options</span></div><strong className="admin-editor-affordance">{editorOpen ? "− Close editor" : "+ Open editor"}</strong></summary>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={submitForm} className="lg:col-span-7 rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-6 space-y-5">
          <div className="admin-secondary-action-row flex items-center justify-between">
            <h2 className="font-display text-2xl text-stone-100">{editingId ? "Edit Event" : "Add New Event"}</h2>
            <div className="flex items-center gap-2">
              <Button type="button" onClick={() => setPreviewingId("draft-preview")} variant="outline" className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
                <Eye className="w-4 h-4 mr-2" /> Preview
              </Button>
              <Button type="button" onClick={resetForm} variant="ghost" className="text-stone-300 hover:text-stone-100">
                <Plus className="w-4 h-4 mr-2" /> New
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Event Name" required>
              <Input value={form.event_name} onChange={(e) => applyField("event_name", e.target.value)} required />
            </Field>
            <Field label="Event Dates (select one or more days)" required className="md:col-span-2">
              <div className="rounded-xl border border-stone-100/15 bg-zinc-950/60 p-3" data-testid="event-dates-picker">
                <div className="bbw-daypicker mx-auto w-full max-w-[340px]">
                  <DayPicker
                    mode="multiple"
                    selected={selectedDates}
                    onDayClick={toggleDay}
                    className="text-stone-100"
                    classNames={{
                      months: "bbw-rdp-months",
                      month: "bbw-rdp-month",
                      caption: "bbw-rdp-caption",
                      caption_label: "bbw-rdp-caption-label",
                      nav: "bbw-rdp-nav",
                      nav_button: "bbw-rdp-nav-button",
                      table: "bbw-rdp-table",
                      head_cell: "bbw-rdp-head-cell",
                      cell: "bbw-rdp-cell",
                      day: "bbw-rdp-day",
                      day_selected: "bbw-rdp-day-selected",
                      day_today: "bbw-rdp-day-today",
                      day_outside: "bbw-rdp-day-outside",
                    }}
                  />
                </div>
                {selectedDates.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(form.event_dates || []).map((key) => (
                      <span key={key} className="inline-flex items-center gap-1 rounded-full border border-[#D8A34B]/40 bg-[#D8A34B]/10 px-2 py-1 text-xs text-[#F5F0E6]">
                        {fmtDate(key)}
                        <button type="button" onClick={() => removeDate(key)} aria-label={`Remove ${fmtDate(key)}`} className="text-[#D8A34B] hover:text-[#F5F0E6]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-stone-400">Pick at least one day.</p>
                )}
              </div>
            </Field>
            <Field label="Start Time" required>
              <Input type="time" value={form.start_time} onChange={(e) => applyField("start_time", e.target.value)} required />
            </Field>
            <Field label="End Time" required>
              <Input type="time" value={form.end_time} onChange={(e) => applyField("end_time", e.target.value)} required />
            </Field>
            <Field label="Location Name" required>
              <Input value={form.location_name} onChange={(e) => applyField("location_name", e.target.value)} required />
            </Field>
            <Field label="Booth/Table Number">
              <Input value={form.booth_number} onChange={(e) => applyField("booth_number", e.target.value)} />
            </Field>
            <Field label="Street Address" required className="md:col-span-2">
              <Input value={form.street_address} onChange={(e) => applyField("street_address", e.target.value)} required />
            </Field>
            <Field label="City" required>
              <Input value={form.city} onChange={(e) => applyField("city", e.target.value)} required />
            </Field>
            <Field label="State" required>
              <Input value={form.state} onChange={(e) => applyField("state", e.target.value)} required />
            </Field>
            <Field label="ZIP" required>
              <Input value={form.zip_code} onChange={(e) => applyField("zip_code", e.target.value)} required />
            </Field>
            <Field label="Status" required>
              <select
                className="w-full h-10 rounded-md border border-stone-100/15 bg-zinc-950/60 px-3 text-stone-100"
                value={form.status}
                onChange={(e) => applyField("status", e.target.value)}
                required
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </Field>
            <Field label="Event Website Link">
              <Input type="url" value={form.event_website_link} onChange={(e) => applyField("event_website_link", e.target.value)} />
            </Field>
            <Field label="Facebook Event Link">
              <Input type="url" value={form.facebook_event_link} onChange={(e) => applyField("facebook_event_link", e.target.value)} />
            </Field>
            <Field label="Short Description" className="md:col-span-2">
              <Textarea value={form.short_description} onChange={(e) => applyField("short_description", e.target.value)} rows={3} />
            </Field>
            <Field label="Long Description" className="md:col-span-2">
              <Textarea value={form.long_description} onChange={(e) => applyField("long_description", e.target.value)} rows={5} />
            </Field>
          </div>

          <div className="rounded-xl border border-stone-100/10 p-4">
            <Label className="text-stone-200">Event Image/Flyer Upload</Label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center">
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadFile} />
                <span className="inline-flex items-center px-3 h-10 rounded-md border border-stone-100/15 text-stone-200 cursor-pointer hover:bg-stone-100/5">
                  <UploadCloud className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload File"}
                </span>
              </label>
              {form.event_image_filename ? (
                <span className="text-xs text-stone-400">{form.event_image_filename}</span>
              ) : (
                <span className="text-xs text-stone-500">No file uploaded.</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="inline-flex items-center gap-2 text-stone-300 text-sm">
              <input type="checkbox" checked={form.featured_event} onChange={(e) => applyField("featured_event", e.target.checked)} />
              Featured Event
            </label>
            <label className="inline-flex items-center gap-2 text-stone-300 text-sm">
              <input
                type="checkbox"
                checked={form.show_canceled_public}
                onChange={(e) => applyField("show_canceled_public", e.target.checked)}
              />
              Show if Canceled
            </label>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" onClick={submitWithStatus("Draft")} disabled={saving} className="bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C]">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : editingId ? "Save Changes" : "Save as Draft"}
            </Button>
            <Button type="button" onClick={submitWithStatus("Published")} disabled={saving} variant="outline" className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
              {editingId ? "Publish" : "Publish Event"}
            </Button>
            {editingId ? (
              <Button type="button" onClick={() => updatePublishState({ id: editingId }, "unpublish")} variant="outline" className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
                Unpublish / Move to Draft
              </Button>
            ) : null}
            {editingId ? (
              <Button type="button" onClick={() => updatePublishState({ id: editingId }, "cancel")} variant="outline" className="bg-transparent border-rose-500/30 text-rose-300">
                Mark as Canceled
              </Button>
            ) : null}
            <Button type="button" onClick={resetForm} variant="ghost" className="text-stone-300 hover:text-stone-100">
              Cancel
            </Button>
          </div>
        </form>

        <aside className="lg:col-span-5 rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-6">
          <h3 className="font-display text-2xl text-stone-100">Event Card Preview</h3>
          {!selectedPreview ? (
            <p className="text-stone-400 mt-3">Select Preview while editing, or click Preview in the table.</p>
          ) : (
            <div className="mt-4 rounded-xl border border-stone-100/10 bg-zinc-950/60 overflow-hidden">
              {selectedPreview.event_image_filename ? (
                <div className="aspect-[16/9] overflow-hidden border-b border-stone-100/10">
                  <img src={`${API}/events/assets/${selectedPreview.event_image_filename}`} alt={selectedPreview.event_name} className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#D8A34B]">{selectedPreview.status}</p>
                <h4 className="font-display text-2xl text-stone-100 mt-1">{selectedPreview.event_name || "Untitled event"}</h4>
                <p className="text-stone-300 mt-2">{selectedPreview.short_description || "Add a short description"}</p>
                <p className="text-stone-400 mt-3">{fmtDate(selectedPreview.event_date)}</p>
                <p className="text-stone-400">{selectedPreview.location_name}</p>
                <p className="text-stone-500 text-sm mt-1">{selectedPreview.full_address || mapPreviewAddress(form)}</p>
              </div>
            </div>
          )}
        </aside>
      </section>


</details>

    </div>
  );
}

function Field({ label, children, required = false, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-stone-200">{label}{required ? " *" : ""}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
